import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { LegalComercioSectionId, LegalVendedorInfo } from "../content/legalComercio";
import { LegalComercioModal } from "../components/legal/LegalComercioModal";

interface LegalComercioContextValue {
  openLegalModal: (sectionId?: LegalComercioSectionId) => void;
  closeLegalModal: () => void;
  setLegalVendedor: (vendedor: LegalVendedorInfo | null) => void;
}

const LegalComercioContext = createContext<LegalComercioContextValue | null>(null);

export function LegalComercioProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [focusSection, setFocusSection] = useState<LegalComercioSectionId | undefined>();
  const [vendedorOverride, setVendedorOverride] = useState<LegalVendedorInfo | null>(null);

  const openLegalModal = useCallback((sectionId?: LegalComercioSectionId) => {
    setFocusSection(sectionId);
    setOpen(true);
  }, []);

  const closeLegalModal = useCallback(() => {
    setOpen(false);
    setFocusSection(undefined);
  }, []);

  const setLegalVendedor = useCallback((vendedor: LegalVendedorInfo | null) => {
    setVendedorOverride(vendedor);
  }, []);

  const value = useMemo(
    () => ({ openLegalModal, closeLegalModal, setLegalVendedor }),
    [openLegalModal, closeLegalModal, setLegalVendedor]
  );

  return (
    <LegalComercioContext.Provider value={value}>
      {children}
      <LegalComercioModal
        isOpen={open}
        onClose={closeLegalModal}
        focusSectionId={focusSection}
        vendedorOverride={vendedorOverride}
      />
    </LegalComercioContext.Provider>
  );
}

export function useLegalComercio(): LegalComercioContextValue {
  const ctx = useContext(LegalComercioContext);
  if (!ctx) {
    throw new Error("useLegalComercio debe usarse dentro de LegalComercioProvider");
  }
  return ctx;
}
